import React from "react";
import { Col, FormGroup, Input, Button, Row } from "reactstrap";
import ImageUploadContainer from "../../User/adding-user/ImageUploadContainer";
import type { PickedImage } from "../../User/adding-user/ImageUploadContainer";

/** One block row: the stored fields plus the row index this editor adds. */
export type ContentBlock = {
  index: number;
  title?: string;
  description?: string;
  /** A stored URL, or the picked image object until the save resolves it. */
  image?: string | PickedImage;
  [field: string]: unknown;
};

export type BlockContentArrayViewProps = {
  blockContentArray: ContentBlock[];
  count: number;
  reset: boolean;
  isRequired: boolean;
  addRow: (e: React.MouseEvent) => void;
  deleteRow: (e: React.MouseEvent) => void;
  handleBlock: (e: React.ChangeEvent<HTMLInputElement>, block: ContentBlock) => void;
  imageUploadReceivedHandler: (img: PickedImage, blockIndex: number) => void;
  removeImageHandler: (blockIndex: number) => void;
};

/** The preview source: a picked object's secure_url or data URL, else the value itself (as before). */
const blockImageSrc = (image: ContentBlock["image"]) =>
  ((image && typeof image === "object" ? image.secure_url || image.data : undefined) ||
    image ||
    "") as string;

const BlockContentArrayView = ({
  blockContentArray,
  count,
  reset,
  isRequired,
  addRow,
  deleteRow,
  handleBlock,
  imageUploadReceivedHandler,
  removeImageHandler,
}: BlockContentArrayViewProps) => {
  return (
    <>
      {/* Header / Buttons */}
      <div className="text-center">
        <h2 style={{ padding: "10px" }}>Block Content</h2>

        <Button className="btn-icon-only rounded-circle" color="facebook" href="#" disabled>
          {count}
        </Button>
        <Button className="btn-slack btn-icon-only rounded-circle" color="default" onClick={addRow}>
          <i className="ni ni-fat-add" />
        </Button>
        <Button
          className="btn-icon-only rounded-circle"
          color="pinterest"
          onClick={deleteRow}
          disabled={isRequired && count === 1}
        >
          <i className="fas fa-trash" />
        </Button>
      </div>

      {/* Blocks */}
      <div>
        {blockContentArray.map((block) => (
          <FormGroup className="row" key={block.index}>
            <Col md="12">
              <h2 style={{ padding: "10px" }}> Section {block.index + 1} </h2>

              <Row style={{ padding: "10px" }}>
                <Col md="2">
                  <label className="form-control-label" style={{ color: "#597ade" }}>
                    Title
                  </label>
                </Col>
                <Col md="10">
                  <Input
                    value={block.title}
                    className="form-control-md"
                    placeholder="title"
                    type="text"
                    name="title"
                    onChange={(e) => handleBlock(e, block)}
                  />
                </Col>
              </Row>

              <Row style={{ padding: "10px" }}>
                <Col md="2">
                  <label className="form-control-label" style={{ color: "#597ade" }}>
                    Description
                  </label>
                </Col>
                <Col md="10">
                  <Input
                    value={block.description}
                    className="form-control-lg"
                    placeholder="description"
                    name="description"
                    type="textarea"
                    rows="5"
                    onChange={(e) => handleBlock(e, block)}
                  />
                </Col>
              </Row>

              <Row style={{ padding: "10px" }}>
                <Col md="2">
                  <label className="form-control-label" style={{ color: "#597ade" }}>
                    Image
                  </label>
                </Col>
                <Col md="10">
                  <ImageUploadContainer
                    ImageUpload={(img) => imageUploadReceivedHandler(img, block.index)}
                    uniqueId={`BlockContent_${block.index}`}
                    clearImage={!!reset}
                    image={blockImageSrc(block.image)}
                    onRemove={() => removeImageHandler(block.index)}
                  />
                </Col>
              </Row>
            </Col>
          </FormGroup>
        ))}
      </div>
    </>
  );
};

export default BlockContentArrayView;
